import type React from "react";
import type { VisibilityState } from "@tanstack/react-table";

import type {
  BilledAmountBasis,
  OrgSessionReportRow,
  OrgSessionReportTotals,
} from "@/lib/api";
import { PaymentCell } from "@/components/PaymentStatus";
import { durationUnknownReason } from "@/lib/sessionDurations";
import { formatNumber, formatPercent } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";

/* ------------------------------------------------------------------ */
/*  Formatters                                                          */
/* ------------------------------------------------------------------ */
// The table shows catalog text rendered with the active `t`; the client CSV is a data
// contract shared with the server CSV (session_report.py): its headers, values, basis
// words and totals stay the fixed English below in every UI language.

// Legacy money rendering, kept byte-identical in the table and the CSV: currency and
// grouping display of report amounts is deferred to the currency milestone (LOC-405).
export function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `$${v.toFixed(2)}`;
}

/** CSV money: a bare number ('12.34') so a spreadsheet reads it numerically; blank
 *  (not '0.00') when the value is unavailable, matching the server CSV convention. */
export function csvMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return "";
  return v.toFixed(2);
}

/** Table energy: the shared grouped number (LOC-405). The CSV keeps its bare toFixed(3). */
export function kwh(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return formatNumber(v, 3);
}

// The backend hands sessions over as "YYYY-MM-DD HH:MM:SS" (UTC). The table shows the
// day US-style to match the date fields above it; the clock is left alone, and
// unrecognized shapes are printed verbatim rather than guessed at.
export function reportTimestamp(raw: string | null | undefined): string {
  if (!raw) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](.+?)Z?$/.exec(raw.trim());
  if (!m) return raw;
  return `${m[2]}/${m[3]}/${m[1]} ${m[4]}`;
}

/** Seconds → compact human duration ('2h 5m' / '14m' / '45s'). '—' when unknown so a
 *  missing value never masquerades as '0s'. Mirrors the server's _format_duration. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  let total = Math.round(seconds);
  if (total < 0) total = 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/** CSV duration: the table's text, but blank (not '—') when unknown — the server CSV
 *  convention, so a spreadsheet never reads a placeholder as data. */
export function csvDuration(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined ? "" : formatDuration(seconds);
}

// A unit id like "0910042001280660123222723534" is far too long for the table column.
// When the unit has a custom name we show that; otherwise the last few digits with a
// leading ellipsis (full id stays on hover / in the CSV).
const UNIT_ID_TAIL = 6;

export function shortUnitId(unitId: string): string {
  if (unitId.length <= UNIT_ID_TAIL) return unitId;
  return `…${unitId.slice(-UNIT_ID_TAIL)}`;
}

/* ------------------------------------------------------------------ */
/*  Column model                                                        */
/* ------------------------------------------------------------------ */

/** Data lookups the cell/CSV renderers need from the host component. */
export interface ReportColumnCtx {
  /** unit_id -> custom display name, or null when the unit has no real name. */
  unitName: (unitId: string) => string | null;
}

/** What a table cell needs: the data lookups plus the active catalog's `t`. */
export interface ReportCellCtx extends ReportColumnCtx {
  t: Translate;
}

export interface SessionReportColumn {
  /** Stable id — the localStorage visibility key and the ColumnChooser key. */
  id: string;
  /** Catalog key of the table header and the column chooser label. */
  labelKey: string;
  /** Catalog key of the header's hover text (what the figures mean). */
  headerTitleKey?: string;
  align?: "left" | "right";
  /** Render the body cell in monospace (used for the timestamp columns). */
  mono?: boolean;
  /** Table body cell. */
  cell: (r: OrgSessionReportRow, ctx: ReportCellCtx) => React.ReactNode;
  /** CSV value(s) for this row — plain strings, escaped by the writer. A column that
   *  exports several cells returns one per entry of `csvLabels`. */
  csv: (r: OrgSessionReportRow, ctx: ReportColumnCtx) => string | string[];
  /** CSV header cells, one per exported cell: fixed English, never translated. */
  csvLabels: string[];
  /** CSV values are numbers this module formatted (money, kWh): written as-is, never
   *  formula-guarded, so a negative amount stays a number. */
  numeric?: boolean;
  /** This column shows money/fee/payout/payment information. It is withheld from an
   *  organization manager (session-only view) — the server also drops the underlying
   *  data, so this flag only scopes the table/chooser/CSV. See visibleReportColumns. */
  money?: boolean;
  /** Table totals-row cell (omit for columns with no meaningful total). The row's
   *  label is placed by the totals row, never by a column. */
  total?: (totals: OrgSessionReportTotals, t: Translate) => React.ReactNode;
  /** CSV totals-row value(s), one per exported cell from the first (omit = blank). */
  csvTotal?: (t: OrgSessionReportTotals) => string | string[];
}

export type BilledUnknownCode = "invoice_missing" | "not_finalized" | "failed" | "not_authorized" | "no_pricing";

/** Why a row has no billed amount; null when it has one. The amount stays unknown —
 *  never a 0 — until a finalized invoice exists. */
export function billedUnknownCode(r: OrgSessionReportRow): BilledUnknownCode | null {
  if (r.billed_amount !== null && r.billed_amount !== undefined) return null;
  switch ((r.billing_status ?? "").trim()) {
    case "finalized":
      return "invoice_missing";
    case "pending":
    case "finalizing":
      return "not_finalized";
    case "failed":
      return "failed";
    case "not_authorized":
      return "not_authorized";
    default:
      return "no_pricing";
  }
}

/** Hover text of a row's "—" Billed cell, in the active catalog; null when it has an amount. */
export function billedUnknownReason(r: OrgSessionReportRow, t: Translate = localization.t): string | null {
  const reason = billedUnknownCode(r);
  return reason ? t("operator.orgs.report.billed_unknown_reason", { reason }) : null;
}

/** How a row's Billed amount is known (older payloads: from billing_source). */
export function billedBasis(r: OrgSessionReportRow): BilledAmountBasis {
  if (r.billed_amount_basis) return r.billed_amount_basis;
  if (r.billed_amount === null || r.billed_amount === undefined) return "unknown";
  return r.billing_source === "legacy_recomputed" ? "estimate" : "invoice";
}

/** Billed total of the selection. Estimates inside the sum are counted, and sessions
 *  without a known amount are left out of it and counted next to it, so a partial or
 *  partly estimated sum never reads as the confirmed invoiced revenue. CSV: same text as
 *  the server CSV's TOTAL line (session_report.billed_total_text). */
function billedEstimateSuffix(t: OrgSessionReportTotals): string {
  const n = t.billed_estimated_count ?? 0;
  return n > 0 ? ` incl. ${n} est.` : "";
}

function billedUnknownSuffix(t: OrgSessionReportTotals): string {
  const n = t.billed_unavailable_count ?? 0;
  return n > 0 ? ` +${n} unknown` : "";
}

/** Hover text of the table's Billed total; undefined when the sum is all invoices. */
function billedTotalTitle(totals: OrgSessionReportTotals, t: Translate): string | undefined {
  const estimated = totals.billed_estimated_count ?? 0;
  const unknown = totals.billed_unavailable_count ?? 0;
  if (estimated <= 0 && unknown <= 0) return undefined;
  return t("operator.orgs.report.billed_total_help", {
    parts: estimated > 0 && unknown > 0 ? "both" : estimated > 0 ? "estimated" : "unknown",
    estimated,
    amount: money(totals.total_billed_estimated_amount ?? null),
    unknown,
  });
}

/** '3 invoice / 2 estimate / 1 unknown' (session_report.billed_basis_mix). */
function billedBasisMix(t: OrgSessionReportTotals): string {
  // A payload without the basis counts cannot tell invoices from estimates: say nothing.
  if (t.billed_invoice_count === undefined && t.billed_estimated_count === undefined) return "";
  const parts: [number, string][] = [
    [t.billed_invoice_count ?? 0, "invoice"],
    [t.billed_estimated_count ?? 0, "estimate"],
    [t.billed_unavailable_count ?? 0, "unknown"],
  ];
  return parts.filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`).join(" / ") || "0 invoice";
}

function percentOfBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** 'frozen policy 7.90% + 0.30' / 'legacy formula 0.79% + 0.30' / 'not charged' — the
 *  server CSV's Processing Fee Basis text (session_report.processing_fee_basis_text). */
export function processingFeeBasisText(r: OrgSessionReportRow): string {
  const basis = r.processing_fee_basis;
  if (!basis) return "";
  if (basis === "not_charged") return "not charged";
  const label = basis === "frozen_policy" ? "frozen policy" : "legacy formula";
  const p = r.processing_fee_policy;
  if (!p || p.rate_bps === null || p.rate_bps === undefined) return `${label} (rate not recorded)`;
  return `${label} ${percentOfBps(p.rate_bps)} + ${((p.fixed_cents ?? 0) / 100).toFixed(2)}`;
}

/** Hover text of one row's processing fee: which policy produced it. */
function processingFeeTitle(r: OrgSessionReportRow, t: Translate): string | undefined {
  const p = r.processing_fee_policy;
  const rate = {
    percent: p ? formatPercent((p.rate_bps ?? 0) / 10_000, 2) : "",
    fixed: p ? money((p.fixed_cents ?? 0) / 100) : "",
  };
  switch (r.processing_fee_basis) {
    case "frozen_policy":
      return t("operator.orgs.report.fee_policy_help", { basis: p ? "frozen_policy" : "frozen_unrecorded", ...rate });
    case "legacy_formula":
      return t("operator.orgs.report.fee_policy_help", { basis: p ? "legacy_formula" : "legacy_unrecorded", ...rate });
    case "not_charged":
      return t("operator.orgs.report.fee_policy_help", { basis: "not_charged", ...rate });
    default:
      return undefined;
  }
}

function processingFeeBasisMix(t: OrgSessionReportTotals): string {
  const c = t.processing_fee_basis_counts ?? {};
  const parts: [number, string][] = [
    [c.frozen_policy ?? 0, "frozen policy"],
    [c.legacy_formula ?? 0, "legacy formula"],
    [c.not_charged ?? 0, "not charged"],
  ];
  return parts.filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`).join(" / ");
}

/** Payment total: CONFIRMED outstanding in dollars, unknown-amount sessions as a count. */
function hasPaymentTotal(t: OrgSessionReportTotals): boolean {
  return t.known_outstanding_session_count > 0 || t.unknown_outstanding_session_count > 0;
}

function csvPaymentTotal(t: OrgSessionReportTotals): string {
  if (!hasPaymentTotal(t)) return "";
  const unknown = t.unknown_outstanding_session_count;
  return `${csvMoney(t.total_known_outstanding_amount)} outstanding${unknown > 0 ? ` +${unknown} unknown` : ""}`;
}

/** A charging / idle / suspended column. The backend sends only exact values; anything
 *  else is null → "—" in the table (hover says why) and a blank CSV cell. Table and
 *  CSV therefore carry the same figures. */
function splitDurationColumn(
  id: string,
  labelKey: string,
  csvLabel: string,
  seconds: (r: OrgSessionReportRow) => number | null | undefined,
): SessionReportColumn {
  return {
    id,
    labelKey,
    csvLabels: [csvLabel],
    align: "right",
    cell: (r, ctx) => {
      const v = seconds(r);
      if (v !== null && v !== undefined) return formatDuration(v);
      const why = durationUnknownReason(r.duration_breakdown, r.duration_issues, ctx.t);
      return why ? <span title={why} className="cursor-help underline decoration-dotted">—</span> : "—";
    },
    csv: (r) => csvDuration(seconds(r)),
  };
}

// The report's columns, in display order. The timing columns (End / Duration /
// Charging / Idle / Suspended) sit right after Start so the "when / how long" story
// reads left-to-right; the financial breakdown follows. Every column but Suspended is
// shown by default — the ColumnChooser lets an operator change that (choices persist
// per browser).
export const SESSION_REPORT_COLUMNS: SessionReportColumn[] = [
  {
    id: "start",
    labelKey: "operator.orgs.report.column_start",
    csvLabels: ["Session Start (UTC)"],
    mono: true,
    cell: (r) => reportTimestamp(r.start_time),
    csv: (r) => r.start_time ?? "",
  },
  {
    id: "end",
    labelKey: "operator.orgs.report.column_end",
    csvLabels: ["Session End (UTC)"],
    mono: true,
    cell: (r) => reportTimestamp(r.end_time),
    csv: (r) => r.end_time ?? "",
  },
  {
    id: "duration",
    labelKey: "operator.orgs.report.column_duration",
    csvLabels: ["Session Duration"],
    align: "right",
    cell: (r) => formatDuration(r.duration_seconds),
    csv: (r) => csvDuration(r.duration_seconds),
  },
  splitDurationColumn("charging", "operator.orgs.report.column_charging", "Charging Time", (r) => r.charging_seconds),
  splitDurationColumn("idle", "operator.orgs.report.column_idle", "Idle Time", (r) => r.idle_seconds),
  splitDurationColumn("suspended", "operator.orgs.report.column_suspended", "Suspended Time", (r) => r.suspended_seconds),
  {
    id: "unit",
    labelKey: "operator.orgs.report.column_unit",
    cell: (r, ctx) => ctx.unitName(r.unit_id) ?? shortUnitId(r.unit_id),
    // The stable id is its own CSV cell (a rename never changes it); the display name
    // sits next to it, blank for an unnamed unit.
    csvLabels: ["Unit ID", "Unit Name"],
    csv: (r, ctx) => [r.unit_id, ctx.unitName(r.unit_id) ?? ""],
    total: (totals, t) => t("operator.orgs.report.total_sessions", { count: totals.total_sessions }),
    csvTotal: (t) => `${t.total_sessions} sessions`,
  },
  {
    id: "driver",
    labelKey: "operator.orgs.report.column_driver",
    csvLabels: ["Driver"],
    cell: (r) => r.customer_name || "—",
    csv: (r) => r.customer_name ?? "",
  },
  {
    id: "energy",
    numeric: true,
    labelKey: "operator.orgs.report.column_energy",
    csvLabels: ["Energy (kWh)"],
    align: "right",
    cell: (r) => kwh(r.energy_kwh),
    csv: (r) => (r.energy_kwh ?? 0).toFixed(3),
    total: (t) => kwh(t.total_energy_kwh),
    csvTotal: (t) => (t.total_energy_kwh ?? 0).toFixed(3),
  },
  {
    id: "billed",
    money: true,
    numeric: true,
    labelKey: "billing.amount.billed",
    align: "right",
    headerTitleKey: "operator.orgs.report.column_billed_help",
    cell: (r, ctx) => {
      const why = billedUnknownReason(r, ctx.t);
      if (why) {
        return <span title={why} className="cursor-help underline decoration-dotted">—</span>;
      }
      if (billedBasis(r) === "estimate") {
        return (
          <span title={ctx.t("operator.orgs.report.billed_estimate_help")} className="cursor-help" data-billed-basis="estimate">
            ≈{money(r.billed_amount)}
            <span className="ml-1 text-[10px] uppercase text-amber-600 dark:text-amber-400">
              {ctx.t("operator.orgs.report.estimate_badge")}
            </span>
          </span>
        );
      }
      return money(r.billed_amount);
    },
    // The amount stays a bare number; its basis is the next cell, so an estimate or a
    // blank (unknown) is never read as an invoiced amount.
    csvLabels: ["Billed", "Billed Basis"],
    csv: (r) => [csvMoney(r.billed_amount), billedBasis(r)],
    total: (totals, t) => {
      const title = billedTotalTitle(totals, t);
      const estimated = totals.billed_estimated_count ?? 0;
      const unknown = totals.billed_unavailable_count ?? 0;
      return title ? (
        <span title={title} className="cursor-help">
          {money(totals.total_billed_amount)}
          <span className="text-amber-600 dark:text-amber-400">
            {estimated > 0 && <>{" "}{t("operator.orgs.report.billed_estimated_mark", { count: estimated })}</>}
            {unknown > 0 && <>{" "}{t("operator.orgs.report.billed_unknown_mark", { count: unknown })}</>}
          </span>
        </span>
      ) : (
        money(totals.total_billed_amount)
      );
    },
    csvTotal: (t) => [
      `${csvMoney(t.total_billed_amount)}${billedEstimateSuffix(t)}${billedUnknownSuffix(t)}`,
      billedBasisMix(t),
    ],
  },
  {
    id: "captured",
    money: true,
    numeric: true,
    labelKey: "billing.amount.captured",
    csvLabels: ["Captured"],
    align: "right",
    cell: (r) => money(r.captured_payment),
    csv: (r) => csvMoney(r.captured_payment),
    total: (t) => money(t.total_captured_payment),
    csvTotal: (t) => csvMoney(t.total_captured_payment),
  },
  {
    id: "payment",
    money: true,
    labelKey: "operator.orgs.report.column_payment",
    csvLabels: ["Payment"],
    cell: (r) => <PaymentCell r={r} />,
    csv: (r) => r.payment_state ?? "",
    total: (totals, t) =>
      hasPaymentTotal(totals) ? (
        <span className="text-amber-600 dark:text-amber-400">
          {t("operator.orgs.report.total_outstanding", {
            amount: money(totals.total_known_outstanding_amount),
            unknown: totals.unknown_outstanding_session_count,
          })}
        </span>
      ) : (
        <span className="opacity-50">—</span>
      ),
    csvTotal: csvPaymentTotal,
  },
  {
    id: "stripe",
    money: true,
    numeric: true,
    labelKey: "operator.orgs.report.column_processing_fee",
    headerTitleKey: "operator.orgs.report.column_processing_fee_help",
    align: "right",
    cell: (r, ctx) => {
      const title = processingFeeTitle(r, ctx.t);
      return title ? (
        <span title={title} className="cursor-help">{money(r.estimated_stripe_fee)}</span>
      ) : (
        money(r.estimated_stripe_fee)
      );
    },
    csvLabels: ["Processing Fee (calc.)", "Processing Fee Basis"],
    csv: (r) => [csvMoney(r.estimated_stripe_fee), processingFeeBasisText(r)],
    total: (t) => money(t.total_estimated_stripe_fee),
    csvTotal: (t) => [csvMoney(t.total_estimated_stripe_fee), processingFeeBasisMix(t)],
  },
  {
    id: "voltie",
    money: true,
    numeric: true,
    labelKey: "operator.orgs.report.column_voltie_fee",
    csvLabels: ["VoltiE Fee"],
    align: "right",
    cell: (r) => money(r.voltie_fee),
    csv: (r) => csvMoney(r.voltie_fee),
    total: (t) => money(t.total_voltie_fee),
    csvTotal: (t) => csvMoney(t.total_voltie_fee),
  },
  {
    id: "payout",
    money: true,
    numeric: true,
    labelKey: "operator.orgs.report.column_payout",
    csvLabels: ["Payout to Org"],
    align: "right",
    cell: (r) => money(r.payout_to_organization),
    csv: (r) => csvMoney(r.payout_to_organization),
    total: (t) => money(t.total_payout_to_organization),
    csvTotal: (t) => csvMoney(t.total_payout_to_organization),
  },
  {
    id: "owes",
    money: true,
    numeric: true,
    labelKey: "operator.orgs.report.column_owes",
    csvLabels: ["Org Owes VoltiE"],
    align: "right",
    cell: (r) => money(r.organization_owes_voltie),
    csv: (r) => csvMoney(r.organization_owes_voltie),
    total: (t) => money(t.total_organization_owes_voltie),
    csvTotal: (t) => csvMoney(t.total_organization_owes_voltie),
  },
];

// Columns hidden until an operator turns them on.
const HIDDEN_BY_DEFAULT = new Set(["suspended"]);

// Default visibility. A saved choice keeps winning; these defaults fill in only the
// columns a saved config does not mention (e.g. ones added after it was saved).
export const SESSION_REPORT_DEFAULT_VISIBILITY: VisibilityState = Object.fromEntries(
  SESSION_REPORT_COLUMNS.map((c) => [c.id, !HIDDEN_BY_DEFAULT.has(c.id)]),
);

// localStorage key for this table's saved column choices (distinct from the Devices
// table's key so the two don't collide).
export const SESSION_REPORT_COLUMNS_LS_KEY = "orgSessionsReportColumns_v1";

/** The columns currently shown, in display order; a column the visibility state does
 *  not mention takes its default. `columns` scopes the set a viewer may see at all
 *  (an org manager is passed the money-free subset) — a saved visibility choice can
 *  therefore never surface a column outside that scope. */
export function visibleReportColumns(
  visibility: VisibilityState,
  columns: SessionReportColumn[] = SESSION_REPORT_COLUMNS,
): SessionReportColumn[] {
  return columns.filter(
    (c) => (visibility[c.id] ?? SESSION_REPORT_DEFAULT_VISIBILITY[c.id]) !== false,
  );
}

/* ------------------------------------------------------------------ */
/*  Client-side CSV (exports ONLY the visible columns)                  */
/* ------------------------------------------------------------------ */

/** RFC-4180 quoting: wrap in quotes and double embedded quotes when the value holds a
 *  comma, quote, or newline; otherwise emit it as-is. */
export function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR = /[\x00-\x1f\x7f]/;
// eslint-disable-next-line no-control-regex
const LEADING_BLANK = /^[\s\x00-\x1f\x7f]*/;

/** A free-text CSV cell, neutralised against formula execution (CSV injection) with a
 *  leading apostrophe — the same rule as the server CSV (session_report.csv_text): a
 *  value starting with = + - @, or with a control character, or reaching one of those
 *  triggers after leading whitespace/control characters. Not for numeric columns. */
export function csvText(value: string): string {
  if (!value) return value;
  const rest = value.replace(LEADING_BLANK, "");
  if (CONTROL_CHAR.test(value[0]) || /^[=+\-@]/.test(rest)) return `'${value}`;
  return value;
}

function csvCells(c: SessionReportColumn, r: OrgSessionReportRow, ctx: ReportColumnCtx): string[] {
  const v = c.csv(r, ctx);
  const cells = Array.isArray(v) ? v : [v];
  // A numeric column's FIRST cell is the number; any further cell is text.
  return cells.map((cell, i) => (c.numeric && i === 0 ? cell : csvText(cell)));
}

function csvTotalCells(c: SessionReportColumn, t: OrgSessionReportTotals): string[] {
  const width = csvHeader(c).length;
  const v = c.csvTotal ? c.csvTotal(t) : [];
  const given = Array.isArray(v) ? v : [v];
  return Array.from({ length: width }, (_, i) => given[i] ?? "");
}

function csvHeader(c: SessionReportColumn): string[] {
  return c.csvLabels;
}

/**
 * Build the report CSV from the already-fetched rows + totals, emitting ONLY the
 * visible columns in their display order. This is what makes "Export CSV" match
 * exactly what the table shows — the server's full CSV export (all columns) is a
 * separate path used elsewhere.
 */
export function buildReportCsv(
  rows: OrgSessionReportRow[],
  totals: OrgSessionReportTotals | null | undefined,
  columns: SessionReportColumn[],
  ctx: ReportColumnCtx,
): string {
  const lines: string[] = [];
  lines.push(columns.flatMap(csvHeader).map(csvEscape).join(","));
  for (const r of rows) {
    lines.push(columns.flatMap((c) => csvCells(c, r, ctx)).map(csvEscape).join(","));
  }
  if (totals && rows.length > 0) {
    const cells = columns.flatMap((c) => csvTotalCells(c, totals));
    // "TOTAL" goes in the first cell only when it carries no total: like the table,
    // the label never replaces a sum.
    if (cells.length > 0 && cells[0] === "") cells[0] = "TOTAL";
    lines.push(cells.map(csvEscape).join(","));
  }
  // CRLF line endings — the convention Excel expects for CSV.
  return lines.join("\r\n");
}
